import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ClipboardList, ShieldCheck, BookOpen, Clock, AlertTriangle, Star, Menu, X, Brain, BarChart3, Target, Dices, PenLine, Library, XCircle, ArrowDown, Users, Award, TrendingUp } from 'lucide-react';
import { SocialProofPopup } from './SocialProofPopup';
import { UpsellPopup } from './UpsellPopup';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/w1jTBLc1G1DaQyQvRi6rU8/Mockup_acompanantes_terapeuticos-Photoroom.png';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay
}} data-id="element-1">
    {children}
  </motion.div>;
const scrollToCompra = () => {
  document.getElementById('compra')?.scrollIntoView({
    behavior: 'smooth'
  });
};
const CTAButton = ({
  onClick,
  children,
  className = ''
}: {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) => <motion.button onClick={onClick} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className={`w-full md:w-auto bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-lg md:text-xl py-5 px-8 md:px-12 rounded-full shadow-[0_0_20px_rgba(196,136,58,0.3)] hover:shadow-[0_0_30px_rgba(196,136,58,0.5)] transition-all duration-300 flex items-center justify-center gap-3 ${className}`} data-id="element-2">
    {children}
  </motion.button>;
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
    setMobileOpen(false);
  };
  return <nav className="sticky top-0 z-50 bg-brand-dark/95 backdrop-blur-md border-b border-brand-accent/15" data-id="element-3">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4">
        <span className="text-brand-cream font-serif font-bold text-lg tracking-wide flex items-center gap-2" data-id="element-5">
          <span className="text-brand-copper" data-id="element-6">📋</span> Sistema PRO: AT
        </span>
        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8" data-id="element-7">
          <button onClick={() => scrollTo('bonos')} className="text-brand-muted hover:text-white text-sm font-medium transition-colors" data-id="element-8">
            Bonos
          </button>
          <button onClick={() => scrollTo('testimonios')} className="text-brand-muted hover:text-white text-sm font-medium transition-colors" data-id="element-9">
            Testimonios
          </button>
          <button onClick={() => scrollTo('compra')} className="bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-sm py-2.5 px-6 rounded-full hover:shadow-[0_0_20px_rgba(196,136,58,0.4)] transition-all" data-id="element-10">
            COMPRAR AHORA
          </button>
        </div>
        {/* Mobile hamburger */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-brand-cream" data-id="element-11">
          {mobileOpen ? <X className="w-6 h-6" data-id="element-12" /> : <Menu className="w-6 h-6" data-id="element-13" />}
        </button>
      </div>
      {/* Mobile menu */}
      <AnimatePresence data-id="element-14">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-brand-dark border-t border-brand-accent/15 overflow-hidden" data-id="element-15">
            <div className="px-4 py-4 flex flex-col gap-3" data-id="element-16">
              <button onClick={() => scrollTo('bonos')} className="text-brand-muted hover:text-white text-left py-2 text-base font-medium transition-colors" data-id="element-17">
                Bonos
              </button>
              <button onClick={() => scrollTo('testimonios')} className="text-brand-muted hover:text-white text-left py-2 text-base font-medium transition-colors" data-id="element-18">
                Testimonios
              </button>
              <button onClick={() => scrollTo('compra')} className="bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-base py-3 px-6 rounded-full text-center mt-2" data-id="element-19">
                COMPRAR AHORA
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}
export function LandingPage() {
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'pro'>('pro');
  const [showBasicUpsellPopup, setShowBasicUpsellPopup] = useState(false);
  const [showProUpsellPopup, setShowProUpsellPopup] = useState(false);
  return <div className="min-h-screen bg-brand-cream overflow-hidden selection:bg-brand-copper selection:text-white" data-id="element-20">
      <SocialProofPopup data-id="element-21" />
      <UpsellPopup isOpen={showBasicUpsellPopup} onClose={() => setShowBasicUpsellPopup(false)} basePlanPrice={18990} basePlanName="Plan Básico" showUpgradeOption={true} data-id="element-22" />
      <UpsellPopup isOpen={showProUpsellPopup} onClose={() => setShowProUpsellPopup(false)} basePlanPrice={23990} basePlanName="Pack Completo" showUpgradeOption={false} data-id="element-23" />
      <Navbar data-id="element-24" />

      {/* HERO SECTION */}
      <section className="relative pt-10 pb-16 md:pt-16 md:pb-24 px-4" data-id="element-25">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_center,rgba(168,196,171,0.2)_0%,rgba(248,245,240,0)_100%)] pointer-events-none" data-id="element-26" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-27">
          <FadeIn data-id="element-28">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-brand-dark leading-tight mb-10" data-id="element-29">
              Sistema PRO:{' '}
              <span className="text-gradient" data-id="element-30">Acompañantes Terapéuticos</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-31">
            <img src={PRODUCT_IMAGE} alt="Sistema PRO Acompañante Terapéutico" className="w-full max-w-2xl mx-auto cursor-pointer hover:scale-[1.02] transition-transform duration-500" onClick={scrollToCompra} data-id="element-32" />
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-33">
            <p className="text-xl md:text-2xl text-brand-muted mt-10 mb-10 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-34">
              Sistema que permite a los Acompañantes Terapéuticos crear informes
              profesionales en minutos… sin tener que escribir todo desde cero.
            </p>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-35">
            <div className="flex justify-center" data-id="element-36">
              <CTAButton onClick={scrollToCompra} className="animate-pulse-slow" data-id="element-37">
                👉 Acceder al Sistema PRO ahora
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SOCIAL PROOF VALIDATION */}
      <section className="py-10 md:py-24 px-4 bg-brand-dark relative overflow-hidden" data-id="element-38">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-[radial-gradient(ellipse_at_top,rgba(196,136,58,0.15)_0%,rgba(0,0,0,0)_70%)] pointer-events-none" data-id="element-39" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-40">
          <FadeIn data-id="element-41">
            {/* Stars */}
            <div className="flex items-center justify-center gap-2 mb-4 md:mb-6" data-id="element-42">
              <div className="flex gap-0.5 md:gap-1" data-id="element-43">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 md:w-6 md:h-6 fill-brand-copper text-brand-copper" data-id="element-44" />)}
              </div>
              <span className="text-brand-copper font-bold text-lg md:text-xl ml-1 md:ml-2" data-id="element-45">
                4.9/5
              </span>
            </div>

            {/* Headers */}
            <h2 className="text-2xl md:text-5xl font-bold text-white text-center mb-2 md:mb-4 leading-tight px-2" data-id="element-46">
              Elegido por más de 1.000
              <br className="hidden md:block" data-id="element-47" /> acompañantes terapéuticos en
              Argentina
            </h2>
            <p className="text-brand-cream/70 text-center text-base md:text-xl mb-8 md:mb-12 max-w-2xl mx-auto px-4" data-id="element-48">
              El sistema de documentación clínica más completo y profesional del
              mercado
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 md:gap-6 mb-8 md:mb-16" data-id="element-49">
              {/* Card 1 */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-8 flex flex-col items-center text-center hover:bg-white/[0.05] transition-colors" data-id="element-50">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-brand-copper/10 flex items-center justify-center mb-2 md:mb-5" data-id="element-51">
                  <Users className="w-5 h-5 md:w-7 md:h-7 text-brand-copper" data-id="element-52" />
                </div>
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2" data-id="element-53">
                  +1.000
                </h3>
                <p className="text-brand-cream/60 text-xs md:text-base" data-id="element-54">
                  Profesionales confían en el sistema
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-8 flex flex-col items-center text-center hover:bg-white/[0.05] transition-colors" data-id="element-55">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-brand-copper/10 flex items-center justify-center mb-2 md:mb-5" data-id="element-56">
                  <Star className="w-5 h-5 md:w-7 md:h-7 text-brand-copper" data-id="element-57" />
                </div>
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2" data-id="element-58">
                  4.9/5
                </h3>
                <p className="text-brand-cream/60 text-xs md:text-base" data-id="element-59">
                  Valoración promedio
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-8 flex flex-col items-center text-center hover:bg-white/[0.05] transition-colors" data-id="element-60">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-brand-copper/10 flex items-center justify-center mb-2 md:mb-5" data-id="element-61">
                  <TrendingUp className="w-5 h-5 md:w-7 md:h-7 text-brand-copper" data-id="element-62" />
                </div>
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2" data-id="element-63">
                  +275
                </h3>
                <p className="text-brand-cream/60 text-xs md:text-base" data-id="element-64">
                  Recursos clínicos y legales
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-8 flex flex-col items-center text-center hover:bg-white/[0.05] transition-colors" data-id="element-65">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-brand-copper/10 flex items-center justify-center mb-2 md:mb-5" data-id="element-66">
                  <Award className="w-5 h-5 md:w-7 md:h-7 text-brand-copper" data-id="element-67" />
                </div>
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2" data-id="element-68">
                  #1
                </h3>
                <p className="text-brand-cream/60 text-xs md:text-base" data-id="element-69">
                  Sistema elegido en Argentina
                </p>
              </div>
            </div>

            {/* Bottom List */}
            <div className="text-center" data-id="element-70">
              <p className="text-brand-cream/40 text-xs md:text-sm font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase mb-4 md:mb-8" data-id="element-71">
                Utilizado en todo tipo de ámbitos
              </p>
              <div className="flex flex-wrap justify-center gap-x-4 md:gap-x-8 gap-y-2 md:gap-y-4" data-id="element-72">
                {['Consultorio Privado', 'Centros de Salud', 'Instituciones Educativas', 'Atención Domiciliaria', 'Obras Sociales'].map((item, i) => <div key={i} className="flex items-center gap-1.5 md:gap-2" data-id="element-73">
                    <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-brand-copper flex-shrink-0" data-id="element-74" />
                    <span className="text-brand-cream/80 font-medium text-xs md:text-base" data-id="element-75">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROBLEMS → SOLUTIONS */}
      <section className="py-14 px-4" data-id="element-76">
        <div className="max-w-5xl mx-auto" data-id="element-77">
          <FadeIn data-id="element-78">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-dark text-center mb-10" data-id="element-79">
              ¿Sos acompañante terapeutico y te pasa alguna de estas
              situaciones?
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-80">
            {/* Problems */}
            <FadeIn delay={0.1} data-id="element-81">
              <div className="bg-red-50 border border-red-200/60 rounded-2xl p-6 h-full" data-id="element-82">
                <div className="space-y-3" data-id="element-83">
                  {['Pasás horas escribiendo informes después del trabajo', 'No sabés cómo redactar informes clínicos correctamente', 'Te cuesta organizar el seguimiento de tus pacientes', 'Muchas veces improvisás actividades en el acompañamiento', 'Sentís que te faltan herramientas profesionales'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-84">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-85" />
                      <span className="text-sm text-brand-dark/80" data-id="element-86">{item}</span>
                    </div>)}
                </div>
              </div>
            </FadeIn>

            {/* Solutions */}
            <FadeIn delay={0.2} data-id="element-87">
              <div className="bg-emerald-50 border border-emerald-200/60 rounded-2xl p-6 h-full" data-id="element-88">
                <p className="text-sm font-bold text-brand-copper uppercase tracking-wider mb-4" data-id="element-89">
                  Con Sistema Pro AT vas a poder:
                </p>
                <div className="space-y-3" data-id="element-90">
                  {['Redactar informes profesionales en minutos', 'Registrar el progreso de tus pacientes de forma clara', 'Organizar todo tu trabajo terapéutico', 'Tener recursos listos para cada sesión'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-91">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-92" />
                      <span className="text-sm text-brand-dark/80 font-medium" data-id="element-93">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} data-id="element-94">
            <p className="text-center text-brand-copper font-semibold mt-8 text-lg" data-id="element-95">
              Sistema Pro AT fue creado para resolver exactamente estos
              problemas.
            </p>
            <div className="flex justify-center mt-2" data-id="element-96">
              <ArrowDown className="w-5 h-5 text-brand-copper animate-bounce" data-id="element-97" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MODULES OVERVIEW */}
      <section className="py-20 px-4 bg-brand-sage border-y border-brand-accent/30" data-id="element-98">
        <div className="max-w-5xl mx-auto" data-id="element-99">
          <FadeIn data-id="element-100">
            <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4 text-center" data-id="element-101">
              ¿Qué ofrece el{' '}
              <span className="text-gradient" data-id="element-102">Sistema Pro AT</span>?
            </h2>
            <p className="text-center text-lg text-brand-muted mb-14 max-w-2xl mx-auto" data-id="element-103">
              8 módulos diseñados para cubrir cada aspecto de tu trabajo
              profesional
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-104">
            {[{
            emoji: '🧠',
            icon: <Brain className="w-7 h-7 text-brand-copper" data-id="element-105" />,
            num: 1,
            title: 'Plantillas de Informes Profesionales',
            desc: 'Informes clínicos listos para completar: seguimiento, evolución, obra social y equipos interdisciplinarios.'
          }, {
            emoji: '📋',
            icon: <ClipboardList className="w-7 h-7 text-brand-copper" data-id="element-106" />,
            num: 2,
            title: 'Sistema de Registro Diario',
            desc: 'Registrá actividades, conductas, emociones e intervenciones de forma organizada día a día.'
          }, {
            emoji: '📊',
            icon: <BarChart3 className="w-7 h-7 text-brand-copper" data-id="element-107" />,
            num: 3,
            title: 'Seguimiento y Evolución',
            desc: 'Monitoreá el progreso del paciente con plantillas de objetivos, cambios y avances.'
          }, {
            emoji: '⚠️',
            icon: <AlertTriangle className="w-7 h-7 text-brand-copper" data-id="element-108" />,
            num: 4,
            title: 'Registro de Situaciones Críticas',
            desc: 'Documentá crisis, incidentes y conductas disruptivas de forma clara y profesional.'
          }, {
            emoji: '🎯',
            icon: <Target className="w-7 h-7 text-brand-copper" data-id="element-109" />,
            num: 5,
            title: 'Recursos de Intervención',
            desc: 'Fichas y actividades para regulación emocional, habilidades sociales y desarrollo personal.'
          }, {
            emoji: '🎲',
            icon: <Dices className="w-7 h-7 text-brand-copper" data-id="element-110" />,
            num: 6,
            title: 'Actividades para Niños y Adolescentes',
            desc: 'Dinámicas terapéuticas pensadas para pacientes jóvenes y el vínculo terapéutico.'
          }, {
            emoji: '✍️',
            icon: <PenLine className="w-7 h-7 text-brand-copper" data-id="element-111" />,
            num: 7,
            title: 'Guía de Redacción Profesional',
            desc: 'Aprendé a redactar informes claros y útiles, evitando errores comunes.'
          }, {
            emoji: '📚',
            icon: <Library className="w-7 h-7 text-brand-copper" data-id="element-112" />,
            num: 8,
            title: 'Banco de Frases Profesionales',
            desc: 'Frases clínicas listas para usar en informes, ahorrando tiempo sin perder calidad.'
          }].map((mod, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-113">
                <div className="glass-card p-5 rounded-2xl h-full flex flex-col hover:border-brand-copper/40 transition-colors duration-300 group" data-id="element-114">
                  <div className="flex items-center gap-3 mb-3" data-id="element-115">
                    <span className="text-2xl" data-id="element-116">{mod.emoji}</span>
                    <span className="text-xs font-bold text-brand-copper/70 uppercase tracking-wider" data-id="element-117">
                      Módulo {mod.num}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-brand-dark mb-2 leading-snug" data-id="element-118">
                    {mod.title}
                  </h3>
                  <p className="text-sm text-brand-muted leading-relaxed flex-grow" data-id="element-119">
                    {mod.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.7} data-id="element-120">
            <div className="mt-14 flex justify-center" data-id="element-121">
              <CTAButton onClick={scrollToCompra} data-id="element-122">
                👉 Acceder a los 8 módulos ahora
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* BONUSES */}
      <section id="bonos" className="py-14 px-4 relative" data-id="element-123">
        <div className="max-w-4xl mx-auto" data-id="element-124">
          <FadeIn data-id="element-125">
            <div className="text-center mb-8" data-id="element-126">
              <span className="bg-red-500/10 text-red-500 font-bold px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border border-red-500/20 inline-block mb-4 animate-pulse" data-id="element-127">
                🎁 GRATIS por el día de hoy
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-dark" data-id="element-128">
                Bonos Exclusivos
              </h2>
            </div>

            <div className="space-y-3" data-id="element-129">
              {[{
              icon: <BookOpen className="w-6 h-6 text-brand-copper" data-id="element-130" />,
              title: 'Biblioteca de Frases Profesionales para Informes',
              desc: 'El vocabulario técnico exacto para que tus informes destaquen.'
            }, {
              icon: <Clock className="w-6 h-6 text-brand-copper" data-id="element-131" />,
              title: 'Pack de Informes Express (5 minutos)',
              desc: 'Plantillas ultra-optimizadas para cuando no tenés tiempo.'
            }, {
              icon: <ShieldCheck className="w-6 h-6 text-brand-copper" data-id="element-132" />,
              title: 'Manual de Situaciones Difíciles en AT',
              desc: 'Cómo documentar crisis y situaciones complejas correctamente.'
            }].map((bonus, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-133">
                  <div className="bg-gradient-to-r from-white to-brand-sage border border-brand-copper/25 px-5 py-4 rounded-xl flex items-center gap-4 relative overflow-hidden" data-id="element-134">
                    <div className="bg-brand-sage p-2.5 rounded-lg border border-brand-copper/20 flex-shrink-0" data-id="element-135">
                      {bonus.icon}
                    </div>
                    <div className="flex-grow min-w-0" data-id="element-136">
                      <h3 className="text-base font-bold text-brand-dark leading-snug" data-id="element-137">
                        {bonus.title}
                      </h3>
                      <p className="text-sm text-brand-muted mt-0.5" data-id="element-138">
                        {bonus.desc}
                      </p>
                    </div>
                    <span className="text-brand-copper font-bold bg-brand-copper/10 px-3 py-1.5 rounded-full text-xs flex-shrink-0 hidden md:block" data-id="element-139">
                      INCLUIDO
                    </span>
                  </div>
                </FadeIn>)}
            </div>

            <FadeIn delay={0.3} data-id="element-140">
              <div className="mt-6 text-center bg-white/60 border border-brand-copper/20 rounded-xl py-4 px-5" data-id="element-141">
                <p className="text-brand-muted text-sm" data-id="element-142">
                  Valor total de los 3 bonos:{' '}
                  <span className="line-through text-brand-muted/60 font-bold" data-id="element-143">
                    $29.990
                  </span>
                </p>
                <p className="text-brand-copper font-bold text-lg mt-1" data-id="element-144">
                  Por el día de hoy →{' '}
                  <span className="bg-brand-copper/10 px-3 py-1 rounded-full" data-id="element-145">
                    GRATIS
                  </span>
                </p>
              </div>
            </FadeIn>
          </FadeIn>
        </div>
      </section>

      {/* PRICING / CTA SECTION */}
      <section id="compra" className="py-12 md:py-16 px-4 bg-brand-sage border-y border-brand-copper/20 relative overflow-hidden" data-id="element-146">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-copper/10 rounded-full blur-3xl pointer-events-none" data-id="element-147" />

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-148">
          <FadeIn data-id="element-149">
            <div className="text-center mb-6 md:mb-8" data-id="element-150">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-dark mb-2" data-id="element-151">
                Elegí el plan ideal para vos
              </h2>
              <p className="text-brand-muted text-base md:text-lg" data-id="element-152">
                Seleccioná la opción que mejor se adapte a tus necesidades
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-153">
              {/* Plan Básico */}
              <div onClick={() => setSelectedPlan('basic')} className={`cursor-pointer rounded-xl p-4 border-2 transition-all duration-300 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 ${selectedPlan === 'basic' ? 'border-brand-copper bg-white shadow-[0_4px_24px_rgba(196,136,58,0.18)]' : 'border-brand-accent/50 bg-white/60 hover:border-brand-copper/40'}`} data-id="element-154">
                <div className="flex items-center gap-3 w-full md:w-auto md:flex-grow" data-id="element-155">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPlan === 'basic' ? 'border-brand-copper' : 'border-brand-muted/50'}`} data-id="element-156">
                    {selectedPlan === 'basic' && <div className="w-2.5 h-2.5 rounded-full bg-brand-copper" data-id="element-157" />}
                  </div>

                  <div data-id="element-158">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-159">
                      <span className="text-lg" data-id="element-160">📘</span>
                      <h3 className="text-brand-dark font-bold text-base md:text-lg uppercase tracking-wide" data-id="element-161">
                        Plan Básico
                      </h3>
                    </div>
                    <p className="text-brand-muted text-xs md:text-sm" data-id="element-162">
                      4 módulos + 1 bono
                    </p>
                  </div>
                </div>

                <div className="text-left md:text-right w-full md:w-auto pl-8 md:pl-0" data-id="element-163">
                  <p className="text-brand-muted/60 line-through text-xs md:text-sm" data-id="element-164">
                    $39.990
                  </p>
                  <p className={`text-xl md:text-2xl font-bold tracking-tight ${selectedPlan === 'basic' ? 'text-brand-copper' : 'text-brand-dark'}`} data-id="element-165">
                    $18.990
                  </p>
                </div>
              </div>

              {/* Pack PRO */}
              <div onClick={() => setSelectedPlan('pro')} className={`cursor-pointer rounded-xl p-4 border-2 transition-all duration-300 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 relative ${selectedPlan === 'pro' ? 'border-brand-copper bg-white shadow-[0_4px_24px_rgba(196,136,58,0.22)]' : 'border-brand-accent/50 bg-white/60 hover:border-brand-copper/40'}`} data-id="element-166">
                <div className="flex items-center gap-3 w-full md:w-auto md:flex-grow" data-id="element-167">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPlan === 'pro' ? 'border-brand-copper' : 'border-brand-muted/50'}`} data-id="element-168">
                    {selectedPlan === 'pro' && <div className="w-2.5 h-2.5 rounded-full bg-brand-copper" data-id="element-169" />}
                  </div>

                  <div data-id="element-170">
                    <div className="flex items-center gap-2 mb-1" data-id="element-171">
                      <span className="text-lg" data-id="element-172">🏆</span>
                      <h3 className="text-brand-dark font-bold text-base md:text-lg uppercase tracking-wide" data-id="element-173">
                        Pack Completo
                      </h3>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-1" data-id="element-174">
                      <span className="bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm" data-id="element-175">
                        <Star className="w-2.5 h-2.5 fill-white" data-id="element-176" /> MÁS ELEGIDO
                      </span>
                      <span className="border border-brand-copper/60 text-brand-copper text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-copper/10" data-id="element-177">
                        AHORRÁS $36.000
                      </span>
                    </div>

                    <p className="text-brand-muted text-xs md:text-sm" data-id="element-178">
                      8 módulos completos + 3 bonos exclusivos
                    </p>
                  </div>
                </div>

                <div className="text-left md:text-right w-full md:w-auto pl-8 md:pl-0" data-id="element-179">
                  <p className="text-brand-muted/60 line-through text-xs md:text-sm" data-id="element-180">
                    $59.990
                  </p>
                  <p className={`text-2xl md:text-3xl font-bold tracking-tight ${selectedPlan === 'pro' ? 'text-brand-copper' : 'text-brand-dark'}`} data-id="element-181">
                    $23.990
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-5" data-id="element-182">
              {selectedPlan === 'pro' ? <button onClick={() => setShowProUpsellPopup(true)} className="block w-full bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-base md:text-lg py-4 px-6 rounded-xl shadow-[0_0_25px_rgba(196,136,58,0.35)] hover:shadow-[0_0_40px_rgba(196,136,58,0.55)] transform hover:scale-[1.02] transition-all duration-300 text-center uppercase tracking-wide" data-id="element-183">
                  👉 Sí, quiero el Sistema PRO
                </button> : <button onClick={() => setShowBasicUpsellPopup(true)} className="block w-full bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-base md:text-lg py-4 px-6 rounded-xl shadow-[0_0_25px_rgba(196,136,58,0.35)] hover:shadow-[0_0_40px_rgba(196,136,58,0.55)] transform hover:scale-[1.02] transition-all duration-300 text-center uppercase tracking-wide" data-id="element-184">
                  👉 Quiero empezar con el Plan Básico
                </button>}
              <p className="text-center text-brand-dark/60 text-xs md:text-sm mt-3 italic" data-id="element-185">
                {selectedPlan === 'pro' ? 'La opción más elegida por profesionales que buscan el sistema completo' : 'Ideal para dar los primeros pasos en la redacción de informes'}
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center items-center gap-3 md:gap-6 mt-5 py-3 px-4 border border-brand-copper/20 rounded-xl bg-white/70" data-id="element-186">
              <div className="flex items-center gap-2" data-id="element-187">
                <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-brand-copper" data-id="element-188" />
                <span className="text-xs md:text-sm text-brand-dark font-medium" data-id="element-189">
                  Pago seguro
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-190">
                <span className="text-base md:text-xl" data-id="element-191">⚡</span>
                <span className="text-xs md:text-sm text-brand-dark font-medium" data-id="element-192">
                  Acceso inmediato
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-193">
                <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-brand-copper" data-id="element-194" />
                <span className="text-xs md:text-sm text-brand-dark font-medium" data-id="element-195">
                  Garantía 7 días
                </span>
              </div>
            </div>

            {/* Comparison Module - shows only selected plan */}
            <div className="mt-6" data-id="element-196">
              <AnimatePresence mode="wait" data-id="element-197">
                {selectedPlan === 'basic' ? <motion.div key="basic-details" initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -12
              }} transition={{
                duration: 0.3
              }} className="rounded-xl p-4 md:p-6 border border-brand-copper/30 bg-white shadow-[0_2px_16px_rgba(196,136,58,0.1)]" data-id="element-198">
                    <h4 className="text-brand-dark font-bold text-base md:text-lg mb-3 flex items-center gap-2" data-id="element-199">
                      <span className="text-lg" data-id="element-200">📘</span> Plan Básico incluye:
                    </h4>
                    <ul className="space-y-2" data-id="element-201">
                      {['Módulo 1: Plantillas de Informes Profesionales', 'Módulo 2: Sistema de Registro Diario', 'Módulo 3: Seguimiento y Evolución', 'Módulo 4: Registro de Situaciones Críticas', 'Bono 1: Biblioteca de Frases Profesionales'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-202">
                          <CheckCircle2 className="w-4 h-4 text-brand-copper flex-shrink-0 mt-0.5" data-id="element-203" />
                          <span className="text-sm text-brand-dark/90 font-medium leading-relaxed" data-id="element-204">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </motion.div> : <motion.div key="pro-details" initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -12
              }} transition={{
                duration: 0.3
              }} className="rounded-xl p-4 md:p-6 border border-brand-copper/40 bg-white relative overflow-hidden shadow-[0_2px_16px_rgba(196,136,58,0.12)]" data-id="element-205">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-brand-copper/5 blur-3xl rounded-full pointer-events-none" data-id="element-206" />
                    <h4 className="text-brand-dark font-bold text-base md:text-lg mb-3 flex items-center gap-2 relative z-10" data-id="element-207">
                      <span className="text-lg" data-id="element-208">🏆</span> Pack PRO incluye:
                    </h4>
                    <ul className="space-y-2 relative z-10" data-id="element-209">
                      {['Los 8 módulos profesionales completos', 'Bono 1: Biblioteca de Frases Profesionales', 'Bono 2: Pack de Informes Express (5 min)', 'Bono 3: Manual de Situaciones Difíciles', 'Acceso de por vida y actualizaciones'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-210">
                          <CheckCircle2 className="w-4 h-4 text-brand-copper flex-shrink-0 mt-0.5" data-id="element-211" />
                          <span className="text-sm text-brand-dark/90 font-medium leading-relaxed" data-id="element-212">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonios" className="py-24 px-4 bg-brand-sage border-y border-brand-accent/30" data-id="element-213">
        <div className="max-w-5xl mx-auto" data-id="element-214">
          <FadeIn data-id="element-215">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-16 text-center" data-id="element-216">
              Lo que dicen otros profesionales
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-217">
              {[{
              name: 'Luciana',
              age: 28,
              city: 'Buenos Aires',
              text: 'Antes pasaba horas el fin de semana armando informes. Con estas plantillas, en 15 minutos tengo todo listo y súper profesional. Me salvó la vida.'
            }, {
              name: 'Martín',
              age: 35,
              city: 'Córdoba',
              text: 'Siempre sentí que mis registros no estaban a la altura de los psicólogos del equipo. Ahora entrego informes que dan gusto leer. Me dio muchísima seguridad.'
            }, {
              name: 'Valeria',
              age: 42,
              city: 'Rosario',
              text: 'Trabajo con tres obras sociales distintas y cada una pide cosas diferentes. El pack me organizó la cabeza y el trabajo. Excelente inversión.'
            }, {
              name: 'Federico',
              age: 30,
              city: 'Mendoza',
              text: 'Llegaba a casa agotado y tener que escribir era una tortura. El sistema de frases profesionales es oro puro. Lo recomiendo a todos los colegas.'
            }, {
              name: 'Camila',
              age: 26,
              city: 'La Plata',
              text: 'Recién empiezo en la profesión y estaba perdidísima con la parte administrativa. Esto es literalmente lo que no te enseñan en el curso de AT.'
            }].map((testimonial, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-218">
                  <div className="glass-card p-6 rounded-2xl h-full flex flex-col" data-id="element-219">
                    <div className="flex gap-1 mb-4" data-id="element-220">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-brand-copper text-brand-copper" data-id="element-221" />)}
                    </div>
                    <p className="text-brand-dark italic flex-grow mb-6" data-id="element-222">
                      "{testimonial.text}"
                    </p>
                    <div className="mt-auto border-t border-brand-accent/30 pt-4" data-id="element-223">
                      <p className="font-bold text-brand-dark" data-id="element-224">
                        {testimonial.name}, {testimonial.age} años
                      </p>
                      <p className="text-sm text-brand-muted" data-id="element-225">
                        {testimonial.city}, Argentina
                      </p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="relative py-20 px-4 overflow-hidden" data-id="element-226">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark via-brand-forest to-brand-dark" data-id="element-227" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(45,74,53,0.8)_0%,rgba(26,46,31,1)_70%)]" data-id="element-228" />

        <div className="max-w-2xl mx-auto relative z-10 text-center" data-id="element-229">
          <FadeIn data-id="element-230">
            <h2 className="font-serif italic text-3xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-231">
              Hacé clic y empezá hoy.
            </h2>

            <motion.a href="Link de tu checkout" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-3 bg-white text-brand-dark font-bold text-lg md:text-xl uppercase tracking-wider py-5 px-10 md:px-14 rounded-full shadow-[0_0_40px_rgba(255,255,255,0.2)] hover:shadow-[0_0_60px_rgba(255,255,255,0.35)] transition-shadow duration-500" data-id="element-232">
              Acceder ahora
              <span className="text-xl" data-id="element-233">→</span>
            </motion.a>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center border-t border-brand-accent/30 text-brand-muted text-sm" data-id="element-234">
        <p data-id="element-235">
          © {new Date().getFullYear()} Sistema PRO: Acompañante Terapéutico.
          Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}